import React, { useState, useEffect } from "react";
import { Box, TextField, Typography, Checkbox, FormControlLabel, Button, Divider, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axios from "axios"; // Import Axios
import { motion, AnimatePresence } from "framer-motion";
import { loginUser } from "../services/authService.tsx"; // Import service login
import ReCAPTCHA from "react-google-recaptcha";
import "../../../shared/components/Login.css";

const images = ["/img/bg-login/BG1.jpg", "/img/bg-login/BG2.jpg", "/img/bg-login/BG3.jpg", "/img/bg-login/BG4.jpg", "/img/bg-login/BG5.jpg", "/img/bg-login/BG6.jpg", "/img/bg-login/BG7.jpg", "/img/bg-login/BG8.jpg", "/img/bg-login/BG9.jpg"];

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);


  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);
  
    return () => clearInterval(interval);
  }, []);

  const handleCaptchaVerify = (value) => {
    setIsCaptchaVerified(!!value);
  };

  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const handleTogglePassword = (field: keyof typeof showPassword) => {
    console.log(`Toggling ${field} from ${showPassword[field]} to ${!showPassword[field]}`);
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePassword = (password: string): string => {
    if (password.length < 8) return "Minimal 8 karakter";
    if (!/[a-z]/.test(password)) return "Harus mengandung huruf kecil";
    if (!/[A-Z]/.test(password)) return "Harus mengandung huruf besar";
    if (!/\d/.test(password)) return "Harus mengandung angka";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return "Harus mengandung karakter khusus";
  
    return ""; // Valid
  };
  
  const handleLogin = async () => {
    if (!email || !password) {
      Swal.fire({
        icon: "error",
        title: "Form Belum Lengkap",
        text: "Masukkan email dan password Anda.",
        confirmButtonColor: "#0F62FE",
      });
      return;
    }

    try {
      const response = await loginUser({ email, password });

      // Simpan token ke localStorage atau sessionStorage
      await localStorage.setItem("token", response.token);
      await localStorage.setItem("roles", JSON.stringify(response.roles));

      Swal.fire({
        icon: "success",
        title: "Login Berhasil!",
        text: "Anda akan diarahkan ke dashboard.",
        confirmButtonColor: "#0F62FE",
      }).then(() => {
        navigate("/");
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Login Gagal!",
        text: error.response?.data?.error || "Terjadi kesalahan, coba lagi!",
        confirmButtonColor: "#0F62FE",
      });
    }
  };

  return (
    <Box className="login-container" sx={{ width: "100vw", height: "100vh", position: "relative", overflow: "hidden" }}>
      {/* Background Carousel */}
      <Box
  className="login-background"
  sx={{
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 1,
    overflow: "hidden",
  }}
>
  <AnimatePresence mode="sync">
    <motion.img
      key={images[currentImageIndex]}
      src={images[currentImageIndex]}
      alt="Background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  </AnimatePresence>
</Box>




      {/* Login Form */}
      <Box className="login-form" sx={{ 
        width: "100%", 
        maxWidth: "400px", 
        padding: "40px", 
        backgroundColor: "rgba(255, 255, 255, 0.9)", 
        borderRadius: "12px", 
        position: "absolute", 
        top: "50%", 
        left: "50%", 
        transform: "translate(-50%, -50%)", 
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        zIndex: 10
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" fontWeight={700} color="#21272A">
            Login
          </Typography>
          <img 
            src="/img/LOGO_SINERGISPP.png" 
            alt="Company Logo" 
            style={{ width: "70px" }} 
          />
        </Box>
        <Box className="input-group" sx={{ textAlign: "left", marginTop: "20px" }}>
          <Typography variant="body2" fontWeight={400} color="#21272A" sx={{ display: "block", textAlign: "left" }}>
            Email Address
          </Typography>
          <TextField fullWidth variant="outlined" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Box>
        <Box className="input-group" sx={{ textAlign: "left", marginTop: "10px" }}>
          <Typography variant="body2" fontWeight={400} color="#21272A" sx={{ display: "block", textAlign: "left" }}>
            Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword.new ? "text" : "password"}
            variant="outlined"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              const newPassword = e.target.value;
              setPassword(newPassword);
              // const validationMessage = validatePassword(newPassword);
              // setErrors(prev => ({ ...prev, newPassword: validationMessage }));
            }}
            error={!!errors.newPassword}
            helperText={errors.newPassword || "Minimal 8 karakter"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleTogglePassword("new")}
                    edge="end"
                  >
                    {showPassword.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="login-options" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
          <FormControlLabel control={<Checkbox />} label="Remember Me" />
          <Typography variant="body2" color="#0F62FE" sx={{ cursor: "pointer" }} onClick={() => navigate("/forgot")}>Forgot Password?</Typography>
        </Box>
        <ReCAPTCHA
          sitekey="6LeAcB8rAAAAAFX-8JOn82SIrzK7T5qmaN9XUQT_"  // Replace with your actual site key
          onChange={handleCaptchaVerify}
          onExpired={() => setIsCaptchaVerified(false)}
        />
        <Button fullWidth variant="contained" disabled={!isCaptchaVerified} sx={{ background: "#0F62FE", padding: "12px", fontSize: "16px", marginTop: "10px" }} onClick={handleLogin}>
          Login
        </Button>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" textAlign="center" color="#001D6C">
          Don't have an account? <a href="/signup">Sign Up</a>
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
